/**
 * "Couldn't check" — the pure model behind every surface that shows it (V109-1-19, the three-critic
 * review of V109-1-13, rounds 2 and 3). No React, no fetches.
 *
 *   - items       one row per reason × source, from what the server sends (readiness
 *                 `couldnt_check_sources`, objective `by_source`), with a fallback for older Workers
 *                 (reason codes only, or a stale crediting state: H1, stale IS Couldn't check);
 *   - fixLink     the fix, named for its source ("Reconnect Google Cloud", H4). R3: the destination is
 *                 where the fix happens — a control's statement editor on the value (R2-H2), the
 *                 evidence upload pre-filled with the test (R2-H3), else the Setup step for THAT
 *                 source (B1). A connection in error / turned off resolves the way Setup does (R2-B1);
 *   - fixActionFor  the direct in-app action for a `fix:*` step on Setup (B2: no empty fix rows).
 */
import { couldntCheckView, fixSubjectKey, isCouldntCheckReason, parseControlSubject, type CouldntCheckReason, type CouldntCheckView } from '@aigovops/core';
import type { RiskAcceptanceSummary, Soc2Requirement } from './api';

export type SourceStatus = 'active' | 'disabled' | 'error';

export interface CouldntItem {
  reason: CouldntCheckReason;
  connector_id: string | null;
  kind: string | null;
  system_id: string | null;
  /** How many controls (readiness) or resources (objectives) this reason leaves unverified. */
  count: number | null;
  /** Plain names of the unverified resources, when known. */
  resources: string[];
  /** When GAC Pro collects from the source next (H8), when known; null when it isn't scheduled. */
  next_collection_at: string | null;
  /** R3 (R2-B1): the connection's status — error / disabled decide the fix, as on Setup. */
  status?: SourceStatus | null;
  /** R3 (R2-H3): the test the result was for (attach evidence is pre-filled with it). */
  test_key?: string | null;
  /** R3 (R2-H3): the objective the result was for (objective programs). */
  objective_id?: string | null;
}

const code = (r: string | null | undefined): CouldntCheckReason => (isCouldntCheckReason(r) ? r : 'collector_error');

/** States a crediting control has when GAC Pro couldn't judge it (H1: stale is one of them). */
export const COULDNT_STATES: ReadonlySet<string> = new Set(['couldnt_check', 'stale', 'insufficient']);

/**
 * The criterion couldn't be checked: the server's `check_state`, else (an older Worker) none of its
 * crediting controls fails and at least one couldn't be judged.
 */
export function isCouldntRequirement(r: Pick<Soc2Requirement, 'status' | 'check_state' | 'crediting_controls'>): boolean {
  if (r.check_state) return r.check_state === 'couldnt_check';
  if (r.status === 'met' || r.status === 'unmapped') return false;
  const open = r.crediting_controls.filter((c) => c.state !== 'pass' && c.state !== 'not_applicable');
  return !open.some((c) => c.state === 'fail') && open.some((c) => COULDNT_STATES.has(c.state));
}

/** One item per reason × source for a criterion GAC Pro couldn't check (empty otherwise). */
export function requirementItems(r: Soc2Requirement): CouldntItem[] {
  if (!isCouldntRequirement(r)) return [];
  if (r.couldnt_check_sources?.length) {
    return r.couldnt_check_sources.map((s) => ({
      reason: code(s.reason_code), connector_id: s.connector_id, kind: s.connector_kind, system_id: s.system_id,
      count: s.controls, resources: [], next_collection_at: s.next_collection_at ?? null, status: s.connector_status ?? null, test_key: s.test_key ?? null,
    }));
  }
  const fromControls = r.crediting_controls.filter((c) => COULDNT_STATES.has(c.state))
    .map((c) => code(c.reason_code ?? (c.state === 'stale' ? 'stale' : c.state === 'insufficient' ? 'missing_measurement' : null)));
  const reasons = r.couldnt_check_reasons?.length ? r.couldnt_check_reasons.map(code) : fromControls;
  const counts = new Map<CouldntCheckReason, number>();
  for (const x of (fromControls.length ? fromControls : reasons)) counts.set(x, (counts.get(x) ?? 0) + 1);
  return [...new Set(reasons.length ? reasons : ['collector_error' as const])].map((reason) => ({
    reason, connector_id: null, kind: null, system_id: null, count: counts.get(reason) ?? null, resources: [], next_collection_at: null,
  }));
}

/** An objective's Couldn't-check payload (objective readiness / the control route). */
export interface ObjectiveCouldnt {
  reasons: string[];
  unverified: number;
  resources: number;
  by_source?: Array<{
    reason: string; connector_id: string; connector_kind: string | null; system_id: string | null; unverified: number; resource_names: string[];
    connector_status?: SourceStatus | null; next_collection_at?: string | null; test_key?: string | null; objective_id?: string;
  }>;
}

export function objectiveItems(cc: ObjectiveCouldnt | null | undefined, objectiveId: string | null = null): CouldntItem[] {
  if (!cc) return [];
  if (cc.by_source?.length) {
    return cc.by_source.map((s) => ({
      reason: code(s.reason), connector_id: s.connector_id, kind: s.connector_kind, system_id: s.system_id,
      count: s.unverified, resources: s.resource_names, next_collection_at: s.next_collection_at ?? null,
      status: s.connector_status ?? null, test_key: s.test_key ?? null, objective_id: s.objective_id ?? objectiveId,
    }));
  }
  const reasons = cc.reasons.length ? cc.reasons : ['collector_error'];
  return reasons.map((r) => ({ reason: code(r), connector_id: null, kind: null, system_id: null, count: reasons.length === 1 ? cc.unverified : null, resources: [], next_collection_at: null, objective_id: objectiveId }));
}

/** Setup, opened on the step for this fix AND this subject (B1). */
export function fixHref(prefix: string, enablerCode: string, subjectKey?: string | null): string {
  const q = new URLSearchParams({ enabler: enablerCode });
  if (subjectKey) q.set('subject', subjectKey);
  return `${prefix}/setup?${q.toString()}#steps`;
}

/** R3 (R2-H2): the control's page with its statement editor open on the value to set. */
export function setValueHref(prefix: string, program: string, requirementId: string): string {
  return `${prefix}/programs/${encodeURIComponent(program)}/controls/${encodeURIComponent(requirementId)}?set=param#control-statement`;
}

/**
 * R3 (R2-H3): the evidence upload pre-filled with the test GAC Pro couldn't check — the objective's
 * upload when the objective is known (it writes the test), else the library upload for the test.
 */
export function attachHref(prefix: string, a: { test_key?: string | null; program?: string | null; period_id?: string | null; objective_id?: string | null; label?: string | null; returnTo?: string | null }): string {
  const q = new URLSearchParams({ attach: '1' });
  if (a.test_key) q.set('test', a.test_key);
  if (a.program && a.period_id && a.objective_id) { q.set('program', a.program); q.set('period', a.period_id); q.set('objective', a.objective_id); }
  if (a.label) q.set('for', a.label.slice(0, 120));
  if (a.returnTo) q.set('return', a.returnTo);
  return `${prefix}/evidence?${q.toString()}#attach`;
}

/** Where the fix happens: the Setup step, a control's statement editor, or the evidence upload. */
export type FixDestination = 'setup' | 'statement' | 'attach';

export interface FixLink { view: CouldntCheckView; href: string; label: string; subject: string | null; destination: FixDestination }

/** What the page knows that the item doesn't: the control and the period (objective programs), the control's ref. */
export interface FixContext {
  program?: string | null;
  requirement_id?: string | null;
  period_id?: string | null;
  /** The control's ref ("AC-2"), so a value fix names it. */
  ref?: string | null;
  /** Where an upload returns to. */
  returnTo?: string | null;
}

/** The fix for one item: its words, its label (H4) and where the fix happens (B1, R3). */
export function fixLink(prefix: string, item: Pick<CouldntItem, 'reason' | 'connector_id' | 'kind' | 'system_id'> & Partial<Pick<CouldntItem, 'status' | 'test_key' | 'objective_id'>>, ctx: FixContext = {}): FixLink {
  const view = couldntCheckView(item.reason, item.kind, null, { status: item.status ?? null, subjectLabel: ctx.ref ?? null });
  const subject = fixSubjectKey(view.enabler_code, {
    connector_id: item.connector_id, kind: item.kind, system_id: item.system_id, test_key: item.test_key ?? null,
    program: ctx.program ?? null, requirement_id: ctx.requirement_id ?? null,
  });
  if (view.enabler_code === 'fix:set-parameters' && ctx.program && ctx.requirement_id) {
    return { view, href: setValueHref(prefix, ctx.program, ctx.requirement_id), label: view.fix_label, subject, destination: 'statement' };
  }
  if (view.enabler_code === 'fix:attach-evidence') {
    const href = attachHref(prefix, { test_key: item.test_key, program: ctx.program, period_id: ctx.period_id, objective_id: item.objective_id, label: ctx.ref ?? null, returnTo: ctx.returnTo });
    return { view, href, label: view.fix_label, subject, destination: 'attach' };
  }
  return { view, href: fixHref(prefix, view.enabler_code, subject), label: view.fix_label, subject, destination: 'setup' };
}

/** The distinct fixes for a set of items (two reasons with one fix on one source → one link). */
export function distinctFixes(prefix: string, items: readonly CouldntItem[], ctx: FixContext = {}): FixLink[] {
  const out = new Map<string, FixLink>();
  for (const i of items) {
    const f = fixLink(prefix, i, ctx);
    if (!out.has(f.href)) out.set(f.href, f);
  }
  return [...out.values()];
}

/** "Oct 14, 3:00 PM" in the viewer's zone. */
export function whenText(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** R3 (Low): when GAC Pro collects next, in words — "shortly" once the scheduled time has passed. */
export function nextCollectionText(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'at the next collection';
  const t = Date.parse(iso);
  return !Number.isFinite(t) || t <= now + 60_000 ? 'shortly' : whenText(iso);
}

/** R3 (R2-H5): the open acceptance that covers this row (any open one on the requirement), else null. */
export function coveringAcceptance(list: readonly RiskAcceptanceSummary[] | null | undefined): RiskAcceptanceSummary | null {
  return list?.[0] ?? null;
}

/** "Oct 31" for a day (`2026-10-31` or a full ISO time). */
export function dayText(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * B2 / R3: the in-app action for a `fix:*` step on Setup — one place, every fix has one. Connection
 * fixes open that connection (or run it, or turn it back on); a value opens the control's statement
 * editor; evidence opens the upload pre-filled with the test; a disabled API opens its console page.
 */
export type FixAction =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'external'; label: string; href: string }
  | { kind: 'collect'; label: string; connectorId: string }
  | { kind: 'turn-on'; label: string; connectorId: string }
  | { kind: 'snippet'; label: string; snippet: string; href: string };

/** R3 (Medium "send-measurement"): what a CI step keeps so GAC Pro gets the measured value, not only pass / fail. */
export const MEASUREMENT_SNIPPET = [
  '# Keep the tool\'s measured output, not only pass or fail. For example:',
  '- run: npx jest --coverage --coverageReporters=json-summary   # coverage/coverage-summary.json has the percentage',
  '- run: semgrep scan --sarif --output semgrep.sarif            # SARIF lists every finding',
  '# Then send those report files with your GAC Pro CI step, as you do today.',
].join('\n');

export function fixActionFor(
  e: { code: string; subject_key: string; title: string; progress?: { fix?: { api?: { name: string | null; console_url: string | null } | null } | null } | null },
  slug: string,
): FixAction | null {
  if (!e.code.startsWith('fix:')) return null;
  const prefix = `/w/${slug}/app`;
  const connectorId = e.subject_key.startsWith('connector:') ? e.subject_key.slice('connector:'.length) : null;
  const connection = connectorId ? `${prefix}/setup#posture-${encodeURIComponent(connectorId)}` : `${prefix}/setup#manage-sources`;
  switch (e.code) {
    case 'fix:reconnect': return { kind: 'link', label: e.title, href: connection };
    case 'fix:enable-api': {
      const api = e.progress?.fix?.api;
      return api?.console_url ? { kind: 'external', label: `Turn on ${api.name ?? 'the API'}`, href: api.console_url } : { kind: 'link', label: 'Open the connection', href: connection };
    }
    case 'fix:collect-again': return connectorId ? { kind: 'collect', label: 'Collect again', connectorId } : { kind: 'link', label: 'Open the connection', href: connection };
    case 'fix:turn-on': return connectorId ? { kind: 'turn-on', label: 'Turn it back on', connectorId } : { kind: 'link', label: 'Open the connection', href: connection };
    case 'fix:set-parameters': {
      const control = parseControlSubject(e.subject_key);
      return control ? { kind: 'link', label: 'Set the value', href: setValueHref(prefix, control.program, control.requirement_id) } : { kind: 'link', label: 'Open your programs', href: `${prefix}/programs` };
    }
    case 'fix:connect-source': return { kind: 'link', label: 'Connect a source', href: `${prefix}/setup#manage-sources` };
    case 'fix:attach-evidence': {
      const test = e.subject_key.startsWith('test:') ? e.subject_key.slice('test:'.length) : null;
      return { kind: 'link', label: 'Attach evidence', href: attachHref(prefix, { test_key: test, returnTo: `${prefix}/setup#steps` }) };
    }
    case 'fix:send-measurement': return { kind: 'snippet', label: 'Copy the CI example', snippet: MEASUREMENT_SNIPPET, href: e.subject_key.startsWith('system:') ? `${prefix}/systems/${encodeURIComponent(e.subject_key.slice('system:'.length))}` : `${prefix}/systems` };
    default: return { kind: 'link', label: 'Open Setup', href: `${prefix}/setup#steps` };
  }
}

/**
 * R3 (R2-H2/H3): where a Setup fix step is done from Today and the coverage view — the same place the
 * Setup row's action goes for a value or evidence (fewer clicks), else the Setup step itself (B1).
 */
export function fixStepHref(prefix: string, e: { code: string; subject_key: string }): string {
  const control = e.code === 'fix:set-parameters' ? parseControlSubject(e.subject_key) : null;
  if (control) return setValueHref(prefix, control.program, control.requirement_id);
  if (e.code === 'fix:attach-evidence' && e.subject_key.startsWith('test:')) return attachHref(prefix, { test_key: e.subject_key.slice('test:'.length) });
  return fixHref(prefix, e.code, e.subject_key);
}
